import React, { useEffect, useRef } from 'react';
import SectionMessage from '@atlaskit/section-message';
import { CC_ERROR_MESSAGE } from '../../common/constants/breadcrumb-names';
import { getUserFriendlyFailureMessage } from '../../common/utils/errors';
import { BindingLevels } from '../../common/utils/errors/error-ui-associations';
import { Breadcrumb } from '../../common/utils/events';
import { I18nHack } from '../../common/utils/i18n-hack';
import { isError, isException } from '../../common/utils/resultful';
import { useCreditCardFailure } from '../../controllers/hooks/use-credit-card-failure';
var BaseCreditCardErrorMessage = function BaseCreditCardErrorMessage(_ref) {
  var className = _ref.className,
    failure = _ref.failure,
    _ref$autoScroll = _ref.autoScroll,
    autoScroll = _ref$autoScroll === void 0 ? true : _ref$autoScroll;
  var errorRef = useRef(null);
  useEffect(function () {
    if (errorRef.current !== null && failure !== undefined && autoScroll) {
      errorRef.current.scrollIntoView();
    }
  }, [errorRef, failure, autoScroll]);
  if (failure === undefined) {
    return null;
  }
  var failureSpecificUserFriendlyMessage = getUserFriendlyFailureMessage(failure);
  var userFriendlyMessage = failureSpecificUserFriendlyMessage !== undefined ? failureSpecificUserFriendlyMessage : /*#__PURE__*/React.createElement(I18nHack, {
    en: /*#__PURE__*/React.createElement(React.Fragment, null, "We are not able to use this credit card.", /*#__PURE__*/React.createElement("br", null), "Try again or use another one."),
    ja: /*#__PURE__*/React.createElement(React.Fragment, null, "\u30AF\u30EC\u30B8\u30C3\u30C8\u30AB\u30FC\u30C9\u306E\u652F\u6255\u3044\u30D5\u30A9\u30FC\u30E0\u3092\u30ED\u30FC\u30C9\u3067\u304D\u307E\u305B\u3093", /*#__PURE__*/React.createElement("br", null), "\u518D\u8AAD\u307F\u8FBC\u307F\u3057\u3066\u3001\u5F8C\u307B\u3069\u3082\u3046\u4E00\u5EA6\u304A\u8A66\u3057\u304F\u3060\u3055\u3044\u3002\u307E\u305F\u306F\u3001\u5225\u306E\u652F\u6255\u3044\u65B9\u6CD5\u3092\u3054\u5229\u7528\u3067\u304D\u307E\u3059\u3002")
  });
  return /*#__PURE__*/React.createElement("div", {
    role: "alert",
    className: className,
    ref: errorRef
  }, /*#__PURE__*/React.createElement(SectionMessage, {
    testId: 'credit-card.error-message',
    appearance: "error"
  }, userFriendlyMessage));
};
var createErrorMessageHandlerComponent = function createErrorMessageHandlerComponent(showFailureCallback) {
  var Component = function Component(_ref2) {
    var className = _ref2.className,
      autoScroll = _ref2.autoScroll;
    var failure = useCreditCardFailure();
    if (failure === undefined) {
      return null;
    }
    var showFailure = showFailureCallback(failure);
    return showFailure ? /*#__PURE__*/React.createElement(Breadcrumb, {
      name: CC_ERROR_MESSAGE
    }, /*#__PURE__*/React.createElement(BaseCreditCardErrorMessage, {
      className: className,
      autoScroll: autoScroll,
      failure: failure
    })) : null;
  };
  return Component;
};

/**
 * This component shows error message shows generic page-level errors - Errors we can't really
 * associate with a particular action or component.
 */
export var CreditCardPageErrorMessage = createErrorMessageHandlerComponent(function (failure) {
  return isException(failure) || isError(failure) && failure.error.association.type === BindingLevels.NONE;
});

/**
 * This component shows error messages related to errors in the CC form
 */
export var CreditCardFormErrorMessage = createErrorMessageHandlerComponent(function (failure) {
  return isError(failure) && failure.error.association.type === BindingLevels.INPUT_GROUP;
});

/**
 * This component shows error messages related to CC detail submissions
 */
export var CreditCardSubmissionErrorMessage = createErrorMessageHandlerComponent(function (failure) {
  return isError(failure) && failure.error.association.type === BindingLevels.ACTION;
});

/**
 * Use this if you want the CC package to decide when to show errors for you but don't care about the positioning.
 * It combines {@link CreditCardSubmissionErrorMessage}, {@link CreditCardFormErrorMessage} and {@link CreditCardPageErrorMessage}
 */
export var CreditCardErrorMessage = function CreditCardErrorMessage(_ref3) {
  var className = _ref3.className,
    autoScroll = _ref3.autoScroll;
  // Note that only 1 of these components should be shown at a time
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(CreditCardPageErrorMessage, {
    className: className,
    autoScroll: autoScroll
  }), /*#__PURE__*/React.createElement(CreditCardFormErrorMessage, {
    className: className,
    autoScroll: autoScroll
  }), /*#__PURE__*/React.createElement(CreditCardSubmissionErrorMessage, {
    className: className,
    autoScroll: autoScroll
  }));
};